import { useEffect, useId, useRef, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Link2,
} from "lucide-react";
import {
  clampPaginationPage,
  pageButtonCapacity,
  visiblePageNumbers,
} from "../lib/responsive-pagination.js";

export function formatLegalDate(value, fallback = "No informada") {
  if (!value) return fallback;
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

export function CatalogPagination({
  pagination,
  onPage,
  itemLabel = "resultados",
  ariaLabel = "Paginación",
}) {
  const pageListRef = useRef(null);
  const jumpInputId = useId();
  const currentPage = clampPaginationPage(
    pagination?.page,
    pagination?.totalPages,
  );
  const totalPages = Math.max(1, Number(pagination?.totalPages) || 1);
  const [pageListWidth, setPageListWidth] = useState(0);
  const [jumpPage, setJumpPage] = useState(String(currentPage));

  useEffect(() => {
    setJumpPage(String(currentPage));
  }, [currentPage]);

  useEffect(() => {
    const element = pageListRef.current;
    if (!element) return undefined;

    const measure = () => setPageListWidth(element.clientWidth);
    measure();

    if (!window.ResizeObserver) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [totalPages]);

  if (!pagination || totalPages <= 1) return null;

  const pages = visiblePageNumbers({
    page: currentPage,
    totalPages,
    capacity: pageButtonCapacity(pageListWidth),
  });
  const goToPage = (page) => {
    const targetPage = clampPaginationPage(page, totalPages);
    setJumpPage(String(targetPage));
    if (targetPage !== currentPage) onPage(targetPage);
  };
  const submitJump = (event) => {
    event.preventDefault();
    goToPage(jumpPage);
  };

  return (
    <nav className="catalog-pagination" aria-label={ariaLabel}>
      <span className="catalog-pagination-status" aria-live="polite">
        <span>
          Página <strong>{currentPage}</strong> de {totalPages}
        </span>
        <small>
          {Number(pagination.total || 0).toLocaleString("es-AR")} {itemLabel}
        </small>
      </span>

      <div className="catalog-pagination-controls">
        <button
          type="button"
          className="catalog-pagination-button"
          disabled={currentPage === 1}
          onClick={() => goToPage(1)}
          aria-label="Ir a la primera página"
          title="Primera página"
        >
          <ChevronsLeft size={17} />
        </button>
        <button
          type="button"
          className="catalog-pagination-button"
          disabled={currentPage === 1}
          onClick={() => goToPage(currentPage - 1)}
          aria-label="Ir a la página anterior"
          title="Página anterior"
        >
          <ChevronLeft size={17} />
        </button>

        <div className="catalog-pagination-pages" ref={pageListRef}>
          {pages.map((page) => (
            <button
              type="button"
              className={`catalog-pagination-page${page === currentPage ? " is-current" : ""}`}
              key={page}
              onClick={() => goToPage(page)}
              aria-current={page === currentPage ? "page" : undefined}
              aria-label={`Ir a la página ${page}`}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="catalog-pagination-button"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(currentPage + 1)}
          aria-label="Ir a la página siguiente"
          title="Página siguiente"
        >
          <ChevronRight size={17} />
        </button>
        <button
          type="button"
          className="catalog-pagination-button"
          disabled={currentPage === totalPages}
          onClick={() => goToPage(totalPages)}
          aria-label="Ir a la última página"
          title="Última página"
        >
          <ChevronsRight size={17} />
        </button>
      </div>

      <form className="catalog-pagination-jump" onSubmit={submitJump} noValidate>
        <label htmlFor={jumpInputId}>Ir a</label>
        <input
          id={jumpInputId}
          type="number"
          min="1"
          max={totalPages}
          step="1"
          inputMode="numeric"
          value={jumpPage}
          onChange={(event) => setJumpPage(event.target.value)}
          aria-label={`Página de destino, entre 1 y ${totalPages}`}
        />
        <button type="submit">Ir</button>
      </form>
    </nav>
  );
}

export function CopyCanonicalLink({ path, label = "Copiar enlace" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        new URL(path, window.location.origin).toString(),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button className="secondary-button" type="button" onClick={copy}>
      {copied ? <Check size={16} /> : <Link2 size={16} />}
      {copied ? "Enlace copiado" : label}
    </button>
  );
}

function validCatalogReturnTo(value, fallback) {
  try {
    const target = new URL(value, window.location.origin);
    return target.origin === window.location.origin && target.pathname === fallback;
  } catch {
    return false;
  }
}

export function catalogLinkState(returnTo) {
  return { legalCatalogReturnTo: returnTo };
}

export function navigateBackToCatalog(navigate, fallback) {
  const returnTo = window.history.state?.legalCatalogReturnTo;
  if (validCatalogReturnTo(returnTo, fallback) && window.history.length > 1) {
    window.history.back();
    return;
  }
  navigate(fallback);
}

export function preservedCatalogHistoryState() {
  const state = window.history.state;
  return state && typeof state === "object" ? state : {};
}
